import React from 'react';

import { DndContext, DragOverlay } from '@dnd-kit/core';
import { horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable';

import { InventoryCard } from '@entities/equipmentInventory';
import { AddWarehouse } from '@features/addWarehouse';
import { MoveEquipmentInventoryQuantityProvider } from '@features/moveEquipmentInventoryQuantity';
import { CircularLoader, UiText } from '@shared/ui';

import cls from './EquipmentBoard.module.css';
import { getWarehouseSortableId } from '../../lib/dnd';
import { EQUIPMENT_DROP_ANIMATION } from '../../lib/dropAnimation';
import { equipmentCollisionDetection } from '../../lib/equipmentCollisionDetection';
import { useEquipmentBoard } from '../../model/useEquipmentBoard';
import { EquipmentBoardSkeleton } from '../EquipmentBoardSkeleton';
import { SortableWarehouseColumn } from '../SortableWarehouseColumn';
import { WarehouseColumn } from '../WarehouseColumn/WarehouseColumn';

interface EquipmentBoardProps {
    filterValue: string;
    isInitialLoading?: boolean;
}

type EquipmentBoardContentProps = Pick<EquipmentBoardProps, 'filterValue'>;

const EquipmentBoardContent = ({ filterValue }: EquipmentBoardContentProps) => {
    const {
        t,
        equipmentData,
        isEquipmentLoading,
        isEquipmentError,
        warehouses,
        hasFullAccess,
        isFiltering,
        warehouseViews,
        activeInventoryGuid,
        inventoryDropPreview,
        activeWarehouse,
        activeWarehouseView,
        activeInventory,
        sensors,
        isInventoryDragDisabled,
        handleDragStart,
        handleDragOver,
        handleDragEnd,
        handleDragCancel,
    } = useEquipmentBoard(filterValue);

    if (isEquipmentLoading) {
        return (
            <div className={cls.loader}>
                <CircularLoader />
            </div>
        );
    }

    if (isEquipmentError) {
        return (
            <div className={cls.loader}>
                <UiText variant="subPlain">{t('Common.error.text')}</UiText>
            </div>
        );
    }

    return (
        <div className={cls.EquipmentBoard}>
            <DndContext
                sensors={sensors}
                collisionDetection={equipmentCollisionDetection}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
            >
                <SortableContext
                    items={warehouseViews.map(({ warehouse }) =>
                        getWarehouseSortableId(warehouse.id)
                    )}
                    strategy={horizontalListSortingStrategy}
                >
                    {warehouseViews.map(({ warehouse, inventoryItems }) => (
                        <SortableWarehouseColumn
                            key={warehouse.id}
                            warehouse={warehouse}
                            warehouses={warehouses}
                            inventoryItems={inventoryItems}
                            hasFullAccess={hasFullAccess}
                            currencySign={equipmentData?.currency.sign ?? ''}
                            isFiltering={isFiltering}
                            activeInventoryGuid={activeInventoryGuid}
                            inventoryDropPreviewIndex={
                                inventoryDropPreview?.warehouseId === warehouse.id
                                    ? inventoryDropPreview.index
                                    : null
                            }
                            isInventoryDragDisabled={isInventoryDragDisabled}
                        />
                    ))}
                </SortableContext>
                {isFiltering && equipmentData && warehouseViews.length === 0 && (
                    <p className={cls.noResults}>{t('SearchModal.return.notFound.error')}</p>
                )}
                <DragOverlay dropAnimation={EQUIPMENT_DROP_ANIMATION}>
                    {activeWarehouse && (
                        <div className={cls.dragOverlay}>
                            <WarehouseColumn
                                warehouse={activeWarehouse}
                                warehouses={warehouses}
                                inventoryItems={activeWarehouseView?.inventoryItems}
                                hasFullAccess={hasFullAccess}
                                currencySign={equipmentData?.currency.sign ?? ''}
                                isFiltering={isFiltering}
                                activeInventoryGuid={null}
                            />
                        </div>
                    )}
                    {activeInventory && (
                        <div className={cls.inventoryDragOverlay}>
                            <InventoryCard
                                guid={activeInventory.guid}
                                title={activeInventory.title}
                                amount={activeInventory.amount}
                                status={activeInventory.status}
                                image={activeInventory.previewUrl}
                                freeDate={activeInventory.freeDate}
                                isQuickEditInventoryMode
                            />
                        </div>
                    )}
                </DragOverlay>
            </DndContext>
            {!isFiltering && <AddWarehouse />}
        </div>
    );
};

export const EquipmentBoard = ({ isInitialLoading = false, ...props }: EquipmentBoardProps) => {
    if (isInitialLoading) {
        return <EquipmentBoardSkeleton />;
    }

    return (
        <MoveEquipmentInventoryQuantityProvider>
            <EquipmentBoardContent {...props} />
        </MoveEquipmentInventoryQuantityProvider>
    );
};
